import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

const { Box, Button, Title, Select, Tooltip, AVAILABLE_TIMEZONES } = vp;

type UserSettings = {
  Timezone: vp.Timezone;
};

const UserSettingsContainer: FunctionComponent = () => {
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'User.Settings' });
  const { response } = useClientData();
  const { userSettings, submitUserSettings } = useUserSettingsProvider();

  const defaultValues: UserSettings = {
    Timezone: userSettings.timezone || 'UTC',
  };

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const handleSubmitUserSettings = async (values: UserSettings) => {
    const submitUserSettingsResponse = await submitUserSettings({
      EmailAddress: response.LoginUserName,
      Timezone: values.Timezone,
    });
    if (!submitUserSettingsResponse.Success) {
      toast.neutral(submitUserSettingsResponse.ErrorMessage);
    }
  };

  return (
    <Formik initialValues={defaultValues} onSubmit={handleSubmitUserSettings}>
      {({ values, handleChange, handleBlur, handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Box flx="col" gap="lg">
              <Title title={t('title')} />
              <Box flx="col" gap="sm">
                <Box width="100%" jc="sb">
                  <Box gapSize="5px" width="50%">
                    <Box>{t('timeZone')}</Box>
                    <Tooltip type="hover" information={t('tooltipMessage')} position="right" icon="question" />
                  </Box>
                  <Select
                    variant="standard"
                    options={timeZoneOptions}
                    name="Timezone"
                    value={values.Timezone}
                    placeholder=""
                    noOrdering
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Box>

                <Box jc="fe">
                  <Button
                    id="save-user-settings"
                    type="submit"
                    label={t('saveButton')}
                    isDisabled={userSettings.timezone === values.Timezone}
                  />
                </Box>
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

export default UserSettingsContainer;
